import { useState, useRef, useEffect } from 'react';
import { AlertCircle, BookOpen, CheckCircle, XCircle, Flag } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../../supabase/client';
import { watchImageHeight } from '../../../utils/imageHeightLimit';
import GraphicsAnswerUpload from '../../../components/GraphicsAnswerUpload';
import ReportErrorModal from '../../../components/ReportErrorModal';
import CodingFileUpload from '../../../components/CodingFileUpload';
// Scratch components
const ScratchCodingAnswerArea = ({ question, paperId, isSubmitted, answer, onAnswerChange }: any) => (
  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
    <p className="text-gray-500 text-sm">Scratch 编辑器已移除，请使用 Python 或 C++ 编程环境</p>
  </div>
);
import ScratchResultDetail from '../../../components/ScratchResultDetail';
import PythonCodingAnswerArea from '../../../components/PythonCodingAnswerArea';
import PythonCodingResultDetail from '../../../components/PythonCodingResultDetail';
import CppCodingAnswerArea from '../../../components/CppCodingAnswerArea';
import CppCodingResultDetail from '../../../components/CppCodingResultDetail';
import SubjectiveQuestionCard from '../../../components/SubjectiveQuestionCard';
import type { QuestionRendererProps, Question } from '../types';
import type { GraphicsQuestion } from '../../../types/graphics';

// 判断是否为客观题
const isObjectiveQuestion = (type: string) => {
  return type === 'single_choice' || type === 'multi_choice' || type === 'judge';
};

// 兼容 question_feedbacks 的两种格式：
// 新格式：{score, feedback} 对象（教师阅卷后存储）
// 旧格式：纯字符串（仅评语，无评分）
const normalizeFeedback = (raw: any): { score?: number; feedback?: string } | undefined => {
  if (!raw) return undefined;
  if (typeof raw === 'object' && raw !== null) {
    return { score: raw.score, feedback: raw.feedback };
  }
  // 旧格式：纯字符串评语，没有 score
  return { feedback: String(raw) };
};

// 判断当前题目是否需要禁用（仅刷题平台受答题模式影响）
const shouldDisableQuestion = (
  questionType: string,
  isAssignmentMode: boolean,
  isPreview?: boolean
) => {
  // 预览模式：始终不禁用，完整展示所有题型
  if (isPreview) return false;
  // 我的作业：始终可以作答
  if (isAssignmentMode) return false;
  // 刷题平台：非客观题始终可作答（由教师人工阅卷）
  return false;
};

const GraphicsAnswerArea = ({ question, paperId, currentUser }: { question: Question; paperId?: string; currentUser?: any }) => {
  const submitted = false;
  const graphicsQ: GraphicsQuestion = {
    id: question.id,
    type: 'graphics',
    title: question.title,
    content: '',
    score: question.score,
    difficulty: (question.graphicsDifficulty as 'easy' | 'medium' | 'hard') || 'medium',
    category: question.graphicsCategory || '',
    answerItems: question.graphicsAnswerItems || [{ id: '1', order: 1, name: '作答项1', placeholder: '请上传程序截图', required: true, maxSizeMB: 5, maxCount: 1, maxBase64SizeKB: 500 }],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  return (
    <div className="space-y-4">
      <GraphicsAnswerUpload question={graphicsQ} examId={paperId || 'default'} questionId={question.id} userId={currentUser?.id || ''} disabled={submitted} />
    </div>
  );
};

export default function QuestionRenderer({
  question,
  currentIndex,
  answers,
  isSubmitted,
  isPreview,
  paperId,
  paper,
  currentUser,
  examQuestions,
  isAssignmentMode,
  answerMode = 'online',
  questionFeedbacks,
  correctRateMap,
  revealedQuestions,
  isPracticeMode,
  onAnswerChange,
  onTextAnswerChange,
  onSaveAnswer,
  isSavingAnswer,
  onExamQuestionsChange,
  onHasWidthChangesChange,
  enableImageScaling = true
}: QuestionRendererProps) {
  // HMR 热更新期间 question 可能短暂为 undefined/null
  // 直接返回占位避免崩溃
  if (!question) {
    return (
      <div className="p-8 flex items-center justify-center">
        <p className="text-gray-400 text-sm">加载中...</p>
      </div>
    );
  }

  // 防止组件卸载后执行异步操作
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  const isDisabled = shouldDisableQuestion(question?.type || '', isAssignmentMode, isPreview);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedOptionImage, setSelectedOptionImage] = useState<string | null>(null);

  // 点击外部隐藏选项图片缩放面板
  // 关键修复：使用捕获阶段确保即使父元素 stopPropagation 也能收到事件
  useEffect(() => {
    if (!selectedOptionImage || !enableImageScaling) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // 如果点击的是选项图片本身或缩放面板内部，不隐藏
      // 缩放面板和图片都有 stopPropagation，但为了安全仍做判断
      if (target.closest('.image-size-slider') || target.closest('[data-option-image]')) {
        return;
      }
      // 其他情况隐藏
      setSelectedOptionImage(null);
    };
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [selectedOptionImage, enableImageScaling]);

  // 选项图片容器 ref — 用于接入与富文本编辑器一致的图片高度限制
  // watchImageHeight 会自动遍历容器内所有 img，统一应用 maxHeight = 容器宽度 × 70% 规则
  const optionImagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = optionImagesContainerRef.current;
    if (!container) return;

    let cleanupFn: (() => void) | null = null;
    let cancelled = false;

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        cleanupFn = watchImageHeight(container);
      } catch (error) {
        console.warn('Failed to initialize option image height observer:', error);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (cleanupFn) cleanupFn();
    };
  }, [question?.options, question?.id]);

  // 练习模式下使用逐题揭示，否则使用全局 isSubmitted
  const isRevealed = isPracticeMode ? (revealedQuestions?.has(question?.id || '') ?? false) : isSubmitted;

  const handleAnswer = (optionId: string) => {
    if (isRevealed) return;
    if (!question) return;
    const isMultiChoice = question.type === 'multi_choice';
    if (isMultiChoice) {
      const currentAnswers = answers[question.id] ? answers[question.id].split(',') : [];
      const optionIndex = currentAnswers.indexOf(optionId);
      let newAnswersArray: string[];
      if (optionIndex > -1) {
        newAnswersArray = currentAnswers.filter(id => id !== optionId);
      } else {
        newAnswersArray = [...currentAnswers, optionId].sort();
      }
      const newAnswersValue = newAnswersArray.length > 0 ? newAnswersArray.join(',') : '';
      if (newAnswersValue) {
        onAnswerChange(question.id, newAnswersValue);
      } else {
        onAnswerChange(question.id, '');
      }
    } else {
      onAnswerChange(question.id, optionId);
    }
  };

  const handleTextAnswer = (text: string) => {
    if (isRevealed) return;
    if (!question) return;
    if (text.trim()) {
      onTextAnswerChange(question.id, text);
    } else {
      onTextAnswerChange(question.id, '');
    }
  };

  const updateQuestionLayout = (newLayout: string) => {
    if (!onExamQuestionsChange) return;
    const newQuestions = examQuestions.map(q =>
      q.id === question.id ? { ...q, titleImageLayout: newLayout } : q
    );
    onExamQuestionsChange(newQuestions);
    if (onHasWidthChangesChange) onHasWidthChangesChange(true);
  };

  const updateOptionLayout = (newLayout: string) => {
    if (!onExamQuestionsChange) return;
    const newQuestions = examQuestions.map(q =>
      q.id === question.id ? { ...q, optionLayout: newLayout } : q
    );
    onExamQuestionsChange(newQuestions);
    if (onHasWidthChangesChange) onHasWidthChangesChange(true);
  };

  const updateOptionImageWidth = async (optionId: string, width: number) => {
    if (!onExamQuestionsChange) return;
    const newOptions = question.options.map(o =>
      o.id === optionId ? { ...o, imageWidth: width } : o
    );
    const newQuestions = examQuestions.map(q =>
      q.id === question.id ? { ...q, options: newOptions } : q
    );
    onExamQuestionsChange(newQuestions);
    if (onHasWidthChangesChange) onHasWidthChangesChange(true);

    // 预览模式下立即保存到数据库
    if (isPreview && question.id) {
      const optionImageWidths = newOptions.reduce((acc: Record<string, number>, o: any) => {
        if (o.imageWidth) acc[o.id] = o.imageWidth;
        return acc;
      }, {});
      try {
        await supabase.from('questions').update({
          option_image_widths: optionImageWidths
        }).eq('id', question.id);
      } catch (e) {
        if (unmountedRef.current) return;
        console.error('保存选项图片宽度失败:', e);
      }
    }
  };

  const updateTitleImageWidth = (idx: number, width: number) => {
    if (!onExamQuestionsChange) return;
    const newWidths = [...(question.titleImageWidths || question.titleImages?.map(() => 100) || [])];
    newWidths[idx] = width;
    const newQuestions = examQuestions.map(q =>
      q.id === question.id ? { ...q, titleImageWidths: newWidths } : q
    );
    onExamQuestionsChange(newQuestions);
    if (onHasWidthChangesChange) onHasWidthChangesChange(true);
  };

  // AI阅卷关闭时刷题平台非客观题禁用提示
  if (isDisabled) {
    return (
      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800">自动判分功能维护中</h4>
            <p className="text-sm text-amber-700 mt-1">
              试卷提交后会自动显示参考答案和评分规则，请自行对照评分！
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 图形化题目
  if (question?.type === 'graphics') {
    return <GraphicsAnswerArea question={question} paperId={paperId} currentUser={currentUser} />;
  }

  // Scratch 编程题
  if (question?.type === 'scratch_coding') {
    if (isRevealed) {
      return (
        <ScratchResultDetail
          question={question}
          paperId={paperId || 'custom'}
          answer={answers[question.id] || ''}
          teacherFeedback={normalizeFeedback(questionFeedbacks?.[question.id])}
        />
      );
    }
    if (isDisabled) {
      return (
        <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">自动判分功能维护中</p>
              <p className="text-sm text-amber-700 mt-1">
                试卷提交后会自动显示参考答案和评分规则，请自行对照评分！
              </p>
            </div>
          </div>
        </div>
      );
    }
    // Scratch 编辑器已移除，统一使用文件上传方式
    return (
      <CodingFileUpload
        key={question.id}
        questionId={question.id}
        paperId={paperId}
        paperTitle={paper?.title}
        questionTitle={question.title}
        userId={currentUser?.id}
        questionType="scratch_coding"
        onFileUpload={(fileUrl) => onAnswerChange(question.id, fileUrl)}
        disabled={isRevealed}
        existingFileUrl={answers[question.id]}
      />
    );
  }

  // Python 编程题
  if (question?.type === 'python_coding') {
    if (isRevealed) {
      return (
        <PythonCodingResultDetail
          question={{
            id: question.id,
            type: question.type,
            title: question.title,
            score: question.score,
            userAnswer: answers[question.id],
          }}
          paperId={paperId || 'custom'}
          answer={answers[question.id] || ''}
          teacherFeedback={normalizeFeedback(questionFeedbacks?.[question.id])}
        />
      );
    }
    if (isDisabled) {
      return (
        <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">自动判分功能维护中</p>
              <p className="text-sm text-amber-700 mt-1">
                试卷提交后会自动显示参考答案和评分规则，请自行对照评分！
              </p>
            </div>
          </div>
        </div>
      );
    }
    return answerMode === 'upload' ? (
      <CodingFileUpload
        key={question.id}
        questionId={question.id}
        paperId={paperId}
        paperTitle={paper?.title}
        questionTitle={question.title}
        userId={currentUser?.id}
        questionType="python_coding"
        onFileUpload={(fileUrl) => onAnswerChange(question.id, fileUrl)}
        disabled={isRevealed}
        existingFileUrl={answers[question.id]}
      />
    ) : (
      <PythonCodingAnswerArea
        question={{
          id: question.id,
          title: question.title,
          content: question.explanation || question.content,
          starterCode: question.codeTemplate,
          referenceSolution: question.referenceAnswer,
          timeLimitMs: 1000,
          memoryLimitMb: 64,
          score: question.score,
        }}
        paperId={paperId || 'custom'}
        isSubmitted={isRevealed}
        answer={answers[question.id]}
        onAnswerChange={(answer) => {
          if (isRevealed) return;
          if (answer) {
            onAnswerChange(question.id, answer);
          } else {
            onAnswerChange(question.id, '');
          }
        }}
      />
    );
  }

  // C++ 编程题
  if (question?.type === 'cpp_coding') {
    if (isRevealed) {
      return (
        <CppCodingResultDetail
          question={{
            id: question.id,
            type: question.type,
            title: question.title,
            score: question.score,
            userAnswer: answers[question.id],
          }}
          paperId={paperId || 'custom'}
          answer={answers[question.id] || ''}
          teacherFeedback={normalizeFeedback(questionFeedbacks?.[question.id])}
        />
      );
    }
    if (isDisabled) {
      return (
        <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">自动判分功能维护中</p>
              <p className="text-sm text-amber-700 mt-1">
                试卷提交后会自动显示参考答案和评分规则，请自行对照评分！
              </p>
            </div>
          </div>
        </div>
      );
    }
    return answerMode === 'upload' ? (
      <CodingFileUpload
        key={question.id}
        questionId={question.id}
        paperId={paperId}
        paperTitle={paper?.title}
        questionTitle={question.title}
        userId={currentUser?.id}
        questionType="cpp_coding"
        onFileUpload={(fileUrl) => onAnswerChange(question.id, fileUrl)}
        disabled={isRevealed}
        existingFileUrl={answers[question.id]}
      />
    ) : (
      <CppCodingAnswerArea
        question={{
          id: question.id,
          title: question.title,
          content: question.explanation || question.content,
          starterCode: question.codeTemplate,
          referenceSolution: question.referenceAnswer,
          timeLimitMs: 1000,
          memoryLimitMb: 64,
          score: question.score,
        }}
        paperId={paperId || 'custom'}
        isSubmitted={isRevealed}
        answer={answers[question.id]}
        onAnswerChange={handleTextAnswer}
      />
    );
  }

  // 主观题/代码题 - 使用统一的 SubjectiveQuestionCard 组件
  if (question?.type === 'coding' || question?.type === 'subjective') {
    // 确定显示模式
    const displayMode = isRevealed ? 'detail' : 'answer';

    return (
      <SubjectiveQuestionCard
        question={{
          id: question.id,
          type: question.type,
          title: question.title,
          score: question.score,
          content: question.content || question.explanation,
          referenceAnswer: question.referenceAnswer,
          scoringPoints: question.scoringPoints,
        }}
        paperId={paperId}
        mode={displayMode}
        answer={answers[question.id]}
        teacherFeedback={normalizeFeedback(questionFeedbacks?.[question.id])}
        onTextAnswerChange={onTextAnswerChange}
        onSaveAnswer={onSaveAnswer}
        isSavingAnswer={isSavingAnswer}
      />
    );
  }

  return (
    <>
      <div ref={optionImagesContainerRef} className={`grid gap-3 ${(question?.optionLayout || 'grid') === 'grid' ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {question?.options?.map((option) => {
          const isSelected = question.type === 'multi_choice'
            ? (answers[question.id] || '').split(',').includes(option.id)
            : answers[question.id] === option.id;
          const showCorrect = isRevealed && option.isCorrect;
          const showWrong = isRevealed && isSelected && !option.isCorrect;

          // 处理图片数组：优先使用新格式images数组，否则回退到旧格式image单张
          const optionImages = option.images && option.images.length > 0
            ? option.images
            : (option.image ? [option.image] : []);
          // 多图默认宽度公式：(199 / 图片数量)%，单图保持150%
          const multiImgDefault = optionImages.length > 1 ? Math.round(199 / optionImages.length) : 150;
          const optionImageWidths = option.imageWidths && option.imageWidths.length > 0
            ? option.imageWidths.map(w => w === 100 ? multiImgDefault : w)
            : (option.imageWidth ? [option.imageWidth === 100 ? multiImgDefault : option.imageWidth] : []);

          return (
            <button
              key={option.id}
              onClick={() => !isRevealed && handleAnswer(option.id)}
              disabled={isRevealed}
              className={`w-full p-3 sm:p-4 rounded-lg border-2 text-left transition-all duration-200 flex items-start gap-2 sm:gap-3 overflow-visible ${
                showCorrect
                  ? 'border-green-500 bg-green-50 shadow-sm'
                  : showWrong
                  ? 'border-red-500 bg-red-50 shadow-sm'
                  : isSelected
                  ? 'border-blue-500 bg-blue-100 shadow-md ring-2 ring-blue-200'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              <span className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 flex items-center justify-center text-xs sm:text-sm font-medium transition-all duration-200 ${
                showCorrect
                  ? 'border-green-500 bg-green-500 text-white'
                  : showWrong
                  ? 'border-red-500 bg-red-500 text-white'
                  : isSelected
                  ? 'border-blue-500 bg-blue-500 text-white shadow-md'
                  : 'border-gray-300 text-gray-500 bg-white'
              }`}>
                {option.id}
              </span>
              <div className="flex-1 text-gray-800 overflow-visible">
                {option.text?.startsWith('<') ? (
                  <div dangerouslySetInnerHTML={{ __html: option.text }} />
                ) : (
                  <span className="whitespace-pre-line">{option.text}</span>
                )}
                {/* 渲染选项图片(支持多张) - 一行2张网格布局 */}
                {optionImages.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-3 overflow-visible" onClick={() => enableImageScaling ? setSelectedOptionImage(null) : undefined}>
                    {optionImages.map((imgUrl, imgIdx) => {
                      const manualWidth = optionImageWidths[imgIdx];
                      const defaultWidth = optionImages.length > 1 ? Math.round(199 / optionImages.length) : 150;
                      const displayWidth = manualWidth != null ? manualWidth : defaultWidth;
                      const imgKey = `${option.id}-${imgIdx}`;
                      const isImgSelected = enableImageScaling && selectedOptionImage === imgKey;
                      return (
                        <div key={imgIdx} className="relative overflow-visible" data-option-image="true" onClick={enableImageScaling ? ((e) => e.stopPropagation()) : undefined}>
                          <img
                            src={imgUrl.startsWith('/sb-api/') ? imgUrl : imgUrl.startsWith('/storage/') ? `${getSupabaseUrl()}${imgUrl}` : imgUrl}
                            alt={`选项${option.id}图片${imgIdx + 1}`}
                            className={`rounded-lg border transition-all ${
                              enableImageScaling
                                ? `cursor-pointer ${isImgSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`
                                : 'border-gray-200'
                            }`}
                            style={{ width: `${displayWidth}%`, height: 'auto', objectFit: 'contain' }}
                            loading="lazy"
                            onClick={enableImageScaling ? ((e) => {
                              e.stopPropagation();
                              setSelectedOptionImage(isImgSelected ? null : imgKey);
                            }) : undefined}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                          {/* 悬浮缩放控制面板（仅在编辑器预览模式下且图片被选中时显示） */}
                          {enableImageScaling && isPreview && onExamQuestionsChange && examQuestions && isImgSelected && (
                            <div className="absolute left-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg p-2 shadow-lg min-w-[200px]" data-option-image="true" style={{ top: '100%' }} onClick={(e) => e.stopPropagation()}>
                              {/* 头部：标签 + 数字输入框 */}
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[10px] text-gray-500">图片{imgIdx + 1} 缩放</span>
                                <div className="flex items-center gap-0.5">
                                  <input
                                    type="number"
                                    min="10"
                                    max="200"
                                    value={displayWidth}
                                    onChange={(e) => {
                                      const val = parseInt(e.target.value);
                                      if (!isNaN(val) && val >= 10 && val <= 200) {
                                        const newWidths = [...(optionImageWidths.length > 0 ? optionImageWidths : optionImages.map(() => optionImages.length > 1 ? Math.round(199 / optionImages.length) : 150))];
                                        newWidths[imgIdx] = val;
                                        onExamQuestionsChange(examQuestions.map(q =>
                                          q.id === question.id ? {
                                            ...q,
                                            options: q.options?.map(o =>
                                              o.id === option.id ? { ...o, imageWidths: newWidths } : o
                                            )
                                          } : q
                                        ));
                                        if (onHasWidthChangesChange) onHasWidthChangesChange(true);
                                      }
                                    }}
                                    onBlur={() => {
                                      if (displayWidth < 10 || displayWidth > 200) {
                                        const clamped = Math.max(10, Math.min(200, displayWidth));
                                        const newWidths = [...(optionImageWidths.length > 0 ? optionImageWidths : optionImages.map(() => optionImages.length > 1 ? Math.round(199 / optionImages.length) : 150))];
                                        newWidths[imgIdx] = clamped;
                                        onExamQuestionsChange(examQuestions.map(q =>
                                          q.id === question.id ? {
                                            ...q,
                                            options: q.options?.map(o =>
                                              o.id === option.id ? { ...o, imageWidths: newWidths } : o
                                            )
                                          } : q
                                        ));
                                        if (onHasWidthChangesChange) onHasWidthChangesChange(true);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const clamped = Math.max(10, Math.min(200, displayWidth));
                                        const newWidths = [...(optionImageWidths.length > 0 ? optionImageWidths : optionImages.map(() => optionImages.length > 1 ? Math.round(199 / optionImages.length) : 150))];
                                        newWidths[imgIdx] = clamped;
                                        onExamQuestionsChange(examQuestions.map(q =>
                                          q.id === question.id ? {
                                            ...q,
                                            options: q.options?.map(o =>
                                              o.id === option.id ? { ...o, imageWidths: newWidths } : o
                                            )
                                          } : q
                                        ));
                                        if (onHasWidthChangesChange) onHasWidthChangesChange(true);
                                      }
                                    }}
                                    className="w-10 h-4 text-[10px] text-center border border-gray-300 rounded focus:border-blue-500 outline-none"
                                  />
                                  <span className="text-[10px] text-gray-500">%</span>
                                </div>
                              </div>
                              {/* 快捷缩放按钮 */}
                              <div className="flex flex-nowrap gap-1 mb-1.5 overflow-x-auto">
                                {[20, 30, 40, 50, 60, 70, 80, 90, 99].map((preset) => {
                                  const handlePresetClick = () => {
                                    const newWidths = [...(optionImageWidths.length > 0 ? optionImageWidths : optionImages.map(() => optionImages.length > 1 ? Math.round(199 / optionImages.length) : 150))];
                                    newWidths[imgIdx] = preset;
                                    onExamQuestionsChange(examQuestions.map(q =>
                                      q.id === question.id ? {
                                        ...q,
                                        options: q.options?.map(o =>
                                          o.id === option.id ? { ...o, imageWidths: newWidths } : o
                                        )
                                      } : q
                                    ));
                                    if (onHasWidthChangesChange) onHasWidthChangesChange(true);
                                  };
                                  return (
                                    <button
                                      key={preset}
                                      onClick={handlePresetClick}
                                      className={`px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors ${
                                        displayWidth === preset
                                          ? 'bg-blue-500 text-white'
                                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                      }`}
                                    >
                                      {preset}
                                    </button>
                                  );
                                })}
                              </div>
                              {/* Range滑块 */}
                              <input
                                type="range"
                                min="10"
                                max="200"
                                value={displayWidth}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value);
                                  const newWidths = [...(optionImageWidths.length > 0 ? optionImageWidths : optionImages.map(() => optionImages.length > 1 ? Math.round(199 / optionImages.length) : 150))];
                                  newWidths[imgIdx] = val;
                                  onExamQuestionsChange(examQuestions.map(q =>
                                    q.id === question.id ? {
                                      ...q,
                                      options: q.options?.map(o =>
                                        o.id === option.id ? { ...o, imageWidths: newWidths } : o
                                      )
                                    } : q
                                  ));
                                  if (onHasWidthChangesChange) onHasWidthChangesChange(true);
                                }}
                                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                style={{
                                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(displayWidth - 10) / 1.9}%, #e5e7eb ${(displayWidth - 10) / 1.9}%, #e5e7eb 100%)`
                                }}
                              />
                              {/* 最小最大值标签 */}
                              <div className="flex justify-between mt-0.5 text-[10px] text-gray-400">
                                <span>10</span>
                                <span>200</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {showCorrect && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
              {showWrong && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      {isRevealed && question && (
        <div className="mt-6 bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">正确答案：</span>
                  <span className="font-medium text-green-600">
                    {question.options?.filter(o => o.isCorrect).map(o => o.id).join(', ') || '-'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">你的答案：</span>
                  <span className={`font-medium ${(() => {
                    const userAns = answers[question.id];
                    if (!userAns) return 'text-gray-400';
                    const correctIds = question.options?.filter(o => o.isCorrect).map(o => o.id) || [];
                    const userIds = userAns.split(',');
                    const isCorrect = userIds.length === correctIds.length && userIds.every(id => correctIds.includes(id));
                    return isCorrect ? 'text-green-600' : 'text-red-600';
                  })()}`}>
                    {answers[question.id] || '未作答'}
                  </span>
                </div>
                {/* 本题正确率：答题人数不足5人时显示"暂无数据" */}
                {isRevealed && (() => {
                  const rateInfo = correctRateMap?.[question.id];
                  const MIN_ATTEMPTS = 5;
                  const hasEnoughData = rateInfo && rateInfo.count >= MIN_ATTEMPTS;
                  return (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">本题正确率：</span>
                      <span className={hasEnoughData ? 'text-blue-600 font-medium' : 'text-gray-400'}>
                        {hasEnoughData ? `${rateInfo.rate}%` : '暂无数据'}
                      </span>
                    </div>
                  );
                })()}
              </div>
              <button
                onClick={() => setShowReportModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
              >
                <Flag className="w-3.5 h-3.5" />
                我要纠错
              </button>
            </div>
            {question.explanation && (
              <div className="pt-3 border-t border-gray-200">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-blue-600" />答案解析
                </h4>
                <div className="text-gray-700 leading-relaxed max-h-40 overflow-y-auto">{question.explanation}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 纠错弹窗 */}
      <ReportErrorModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        questionId={question?.id || ''}
        questionTitle={paper?.title || '未知试卷'}
        paperId={paperId}
        currentUser={currentUser}
        question={question ? {
          id: question.id,
          title: question.title,
          order_index: currentIndex + 1,
          question_type: question.type,
          options: question.options,
          reference_answer: question.referenceAnswer,
          explanation: question.explanation,
          scoring_points: question.scoringPoints,
          title_images: question.titleImages,
          title_image_widths: question.titleImageWidths,
          title_image_layout: (question as any).titleImageLayout,
          image_position: (question as any).imagePosition,
          option_layout: (question as any).optionLayout,
        } : null}
      />
    </>
  );
}